import axios from "axios";
import { isAxiosBusinessProfileSubscriptionLimitError } from "../constants/customerApiErrors";
import { notifySessionUnauthorized, notifyCreditInsufficient } from "../auth/sessionUnauthorizedEvents";
import { getFreshIdToken } from "../auth/getFreshIdToken";

let installed = false;

const COGNITO_CLIENT_ID = (process.env.REACT_APP_CLIENT_ID || "").trim();

/**
 * Decode a JWT payload without throwing/logging. Returns null for anything that
 * isn't a parseable JWT (e.g. opaque third-party tokens).
 */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * True only for OUR Cognito ID tokens. This keeps the request interceptor from
 * touching third-party Authorization headers (e.g. Facebook/Graph, LinkedIn),
 * which must keep their own tokens.
 */
function isOurIdToken(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (payload.token_use !== "id") return false;
  return !COGNITO_CLIENT_ID || payload.aud === COGNITO_CLIENT_ID;
}

/**
 * Registers interceptors on the default axios instance used across the app:
 *
 * - Request: if a request already carries one of our Cognito ID tokens as a
 *   `Bearer` header, swap in a freshly-refreshed token. This makes token
 *   rotation invisible to the ~26 axios call sites without changing any of them,
 *   and avoids 401s from an expired token. Third-party Authorization headers are
 *   left untouched.
 * - Response: surface 401/403/402 to the global re-login / credit dialogs.
 */
export function setupAxiosAuthInterceptor(): void {
  if (installed) {
    return;
  }
  installed = true;

  axios.interceptors.request.use(async (config) => {
    try {
      const headers: any = config.headers || {};
      const raw: unknown = headers.Authorization ?? headers.authorization;
      if (typeof raw === "string" && raw.startsWith("Bearer ")) {
        const current = raw.slice("Bearer ".length);
        if (isOurIdToken(current)) {
          const fresh = await getFreshIdToken(current);
          if (fresh && fresh !== current) {
            if (typeof headers.set === "function") {
              headers.set("Authorization", `Bearer ${fresh}`);
            } else {
              headers.Authorization = `Bearer ${fresh}`;
            }
          }
        }
      }
    } catch {
      /* never block a request because the refresh check failed */
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      if (status === 402) {
        notifyCreditInsufficient();
      } else if (
        (status === 401 || status === 403) &&
        !isAxiosBusinessProfileSubscriptionLimitError(error)
      ) {
        notifySessionUnauthorized(status);
      }
      return Promise.reject(error);
    }
  );
}
